/**
 * TrustGrid Alerts Page
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tag, Button, Spin, Modal, Form, Input } from 'antd';
import { CheckOutlined, FileAddOutlined } from '@ant-design/icons';
import { listAlerts, updateAlert, createFindingFromAlert } from '../api/trustgridApi';
import { useTrustGrid } from '../../../contexts/TrustGridContext';
import ProjectSelector from './ProjectSelector';
import { showError, showSuccess } from '../../../utils/notifications';
import { handleCrossOrgError } from '../utils/errorHandlers';

const { TextArea } = Input;

function Alerts() {
  const navigate = useNavigate();
  const { selectedProjectId, selectProject } = useTrustGrid();
  const projectId = selectedProjectId || 3;
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createFindingModalVisible, setCreateFindingModalVisible] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const res = await listAlerts(projectId);
      setAlerts(res.data.alerts || []);
    } catch (error) {
      const wasHandled = handleCrossOrgError(error, selectProject, navigate, 'Alerts');
      if (!wasHandled) {
        console.error('Failed to fetch alerts:', error);
        showError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await updateAlert(alertId, { state: 'acknowledged' });
      showSuccess('Alert acknowledged');
      fetchAlerts();
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
      showError(error);
    }
  };

  const handleCreateFinding = (alert) => {
    setSelectedAlert(alert);
    form.setFieldsValue({
      title: `${alert.control_code}: ${alert.message?.substring(0, 100)}`,
      description: alert.message
    });
    setCreateFindingModalVisible(true);
  };

  const handleSubmitFinding = async (values) => {
    try {
      await createFindingFromAlert(selectedAlert.id, values);
      showSuccess('Finding created from alert');
      setCreateFindingModalVisible(false);
      form.resetFields();
      setSelectedAlert(null);
    } catch (error) {
      console.error('Failed to create finding:', error);
      showError(error);
    }
  };

  const columns = [
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 120,
      render: (severity) => {
        const color = severity === 'critical' ? 'red' : severity === 'high' ? 'orange' : 'default';
        return <Tag color={color}>{severity}</Tag>;
      }
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      width: 140,
      render: (state) => {
        const color = state === 'acknowledged' ? 'green' : 'orange';
        return <Tag color={color}>{state}</Tag>;
      }
    },
    {
      title: 'First Seen',
      dataIndex: 'first_seen_at',
      key: 'first_seen_at',
      width: 150
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            size="small"
            icon={<FileAddOutlined />}
            onClick={() => handleCreateFinding(record)}
            title="Create Finding"
          />
          <Button
            size="small"
            icon={<CheckOutlined />}
            onClick={() => handleAcknowledge(record.id)}
            disabled={record.state === 'acknowledged'}
            title="Acknowledge"
          />
        </div>
      )
    }
  ];

  return (
    <div>
      <ProjectSelector />

      <h2 style={{ marginBottom: '16px' }}>Alerts</h2>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : alerts.length === 0 ? (
        <p style={{ color: '#888' }}>No alerts</p>
      ) : (
        <Table
          dataSource={alerts}
          columns={columns}
          rowKey="id"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true
          }}
          bordered
        />
      )}

      {/* Create Finding Modal */}
      <Modal
        title="Create Finding from Alert"
        open={createFindingModalVisible}
        onCancel={() => {
          setCreateFindingModalVisible(false);
          form.resetFields();
          setSelectedAlert(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmitFinding}>
          <Form.Item name="title" label="Finding Title" rules={[{ required: true }]}>
            <Input placeholder="Finding title" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={4} placeholder="Describe the finding" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" className="custom-button">
              Create Finding
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Alerts;
